import projectData from '../../data/projects.json';

const enotriaImg = new URL('../../img/work-enotria.webp', import.meta.url).href;
const dndImg = new URL('../../img/DndToolLogo.webp', import.meta.url).href;
const logoImg = new URL('../../img/logoS-W.webp', import.meta.url).href;

const ASSET_MAP = {
    'enotria': enotriaImg,
    'dndtool': dndImg,
    'logo': logoImg
};

class ConstellationWorks {
    constructor(canvasId) {
        this.canvas = document.getElementById(canvasId);
        if (!this.canvas) return;

        this.ctx = this.canvas.getContext('2d');
        this.container = this.canvas.parentElement;
        this.mouse = { x: null, y: null };
        this.parallaxOffset = { x: 0, y: 0 };
        this.hoveredConstellation = null;
        this.isZooming = false;
        this.zoomProgress = 0;
        this.zoomTarget = null;
        this.zoomStartTime = 0;
        this.time = 0;
        this.isActive = false;
        this.revealStartTime = null;
        this.revealPending = false;
        this.isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
        this.isMobile = window.innerWidth < 768;
        this.fpsLimit = this.isMobile ? 30 : 60;
        this.lastFrameTime = 0;

        this.colors = {
            background: '#040911',
            nodePrimary: '#2D68C4',
            nodeSecondary: '#8847c7',
            nodeActive: '#00d4ff',
            connectionBase: 'rgba(45, 104, 196, 0.7)',
            connectionHover: 'rgba(0, 212, 255, 0.9)'
        };

        this.projectData = projectData;
        this.bgStars = [];
        this.init();
    }

    init() {
        this.precomputeProjectData();
        this.loadImages();
        this.resize();
        this.createBackgroundStars();
        this.bindEvents();
        this.populateMobileGrid();
        this.watchSection();
    }

    loadImages() {
        if (!this.projectData) return;
        this.projectData.forEach(project => {
            const img = new Image();
            project.imageObj = img;
            project.imageLoaded = false;

            img.onload = () => {
                project.imageLoaded = true;
            };

            img.onerror = () => {
                console.error(`ConstellationWorks: Image load failed for ${project.id}. Using logo fallback.`);
                if (img.src !== ASSET_MAP.logo) {
                    img.src = ASSET_MAP.logo;
                }
            };

            // RESOLUTION LAYERS:
            // 1. Check DOM Registry (best for production hashed assets)
            const registryImg = document.getElementById(`asset-${project.id}`);
            const hashedSrc = registryImg?.src;

            // 2. Fallback to static ASSET_MAP or logo
            const resolvedSrc = hashedSrc || ASSET_MAP[project.id] || ASSET_MAP.logo;
            img.src = resolvedSrc;

            if (img.complete) {
                project.imageLoaded = true;
            }
        });
    }

    populateMobileGrid() {
        const grid = document.querySelector('.works-mobile-grid');
        if (!grid || !this.projectData.length) return;

        grid.innerHTML = '';
        this.projectData.forEach((project, idx) => {
            const nodeIdx = Math.floor(idx / 2) + 1;
            const nodeClass = idx % 2 === 0 ? `node-left-${nodeIdx}` : `node-right-${nodeIdx}`;

            const anchor = document.createElement('a');
            anchor.href = project.url;
            anchor.target = project.url === '#0' ? '_self' : '_blank';
            if (anchor.target === '_blank') anchor.rel = 'noopener';
            anchor.className = `mobile-constellation-node ${nodeClass}`;
            anchor.setAttribute('aria-label', `Vedi il progetto ${project.name}`);

            const wrapper = document.createElement('div');
            wrapper.className = 'node-image-wrapper';

            const img = document.createElement('img');

            // RESOLUTION LAYERS:
            const registryImg = document.getElementById(`asset-${project.id}`);
            img.src = registryImg?.src || ASSET_MAP[project.id] || ASSET_MAP.logo;

            img.alt = `Sito web ${project.name}`;
            img.loading = 'lazy';

            wrapper.appendChild(img);
            anchor.appendChild(wrapper);

            const span = document.createElement('span');
            span.textContent = project.name.toUpperCase();
            anchor.appendChild(span);

            grid.appendChild(anchor);
        });
    }

    precomputeProjectData() {
        this.projectData.forEach(project => {
            project.maxRadius = Math.max(...project.nodes.map(n => Math.sqrt(n.x ** 2 + n.y ** 2) + n.size)) + 30;
            project.center = { x: 0, y: 0 };
        });
    }

    watchSection() {
        const section = this.canvas.closest('.section');
        if (!section) {
            this.scheduleReveal();
            return;
        }

        const onSectionActive = () => {
            if (this.revealPending) return;
            this.revealPending = true;
            this.revealStartTime = null;

            if (!this.isActive) {
                this.isActive = true;
                this.animate();
            }

            setTimeout(() => {
                this.revealStartTime = performance.now();
                this.revealPending = false;
            }, 1000);
        };

        const classObserver = new MutationObserver(() => {
            if (section.classList.contains('section--is-active')) {
                onSectionActive();
            } else {
                this.isActive = false;
                this.revealStartTime = null;
                this.revealPending = false;
            }
        });

        classObserver.observe(section, { attributes: true, attributeFilter: ['class'] });
        if (section.classList.contains('section--is-active')) onSectionActive();
    }

    scheduleReveal() {
        this.isActive = true;
        this.animate();
        setTimeout(() => {
            this.revealStartTime = performance.now();
        }, 1000);
    }

    resize() {
        this.canvas.width = this.container.offsetWidth;
        this.canvas.height = this.container.offsetHeight;
        this.createBackgroundStars();
    }

    createBackgroundStars() {
        this.bgStars = [];
        const isMobile = this.canvas.width < 768;
        const density = isMobile ? 10000 : 5000;
        const count = Math.floor((this.canvas.width * this.canvas.height) / density);
        for (let i = 0; i < count; i++) {
            this.bgStars.push({
                x: Math.random() * this.canvas.width,
                y: Math.random() * this.canvas.height,
                size: Math.random() * 1.5 + 0.3,
                alpha: Math.random() * 0.5 + 0.1,
                phase: Math.random() * Math.PI * 2,
                speed: 0.01 + Math.random() * 0.02
            });
        }
    }

    bindEvents() {
        window.addEventListener('resize', () => this.resize());
        this.container.addEventListener('mousemove', (e) => {
            if (this.isZooming) return;
            const rect = this.canvas.getBoundingClientRect();
            this.mouse.x = e.clientX - rect.left;
            this.mouse.y = e.clientY - rect.top;
            const cx = this.canvas.width / 2;
            const cy = this.canvas.height / 2;
            this.parallaxOffset.x = (this.mouse.x - cx) * 0.02;
            this.parallaxOffset.y = (this.mouse.y - cy) * 0.02;
            this.checkHover();
        });

        this.container.addEventListener('mouseleave', () => {
            if (!this.isZooming) {
                this.mouse.x = null; this.mouse.y = null;
                this.parallaxOffset.x = 0; this.parallaxOffset.y = 0;
                this.hoveredConstellation = null;
                this.updateLabel(null);
            }
        });

        this.container.addEventListener('click', () => {
            if (this.hoveredConstellation && !this.isZooming) {
                this.startZoom(this.hoveredConstellation);
            }
        });

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.isActive = false;
            } else {
                const section = this.canvas.closest('.section');
                const isSectionActive = section ? section.classList.contains('section--is-active') : true;
                if (isSectionActive && !this.isActive) {
                    this.isActive = true; this.animate();
                }
            }
        });
    }

    updateCenters() {
        const isMobile = this.canvas.width < 768;
        this.projectData.forEach((project, idx) => {
            if (isMobile) {
                const row = Math.floor(idx / 2);
                const col = idx % 2;
                project.center.x = this.canvas.width * (col === 0 ? 0.25 : 0.75) + this.parallaxOffset.x;
                project.center.y = this.canvas.height * (0.28 + row * 0.25) + this.parallaxOffset.y;
            } else {
                project.center.x = this.canvas.width * project.centerX + this.parallaxOffset.x;
                project.center.y = this.canvas.height * project.centerY + this.parallaxOffset.y;
            }
        });
    }

    checkHover() {
        let found = null;
        for (const project of this.projectData) {
            const dist = Math.sqrt((this.mouse.x - project.center.x) ** 2 + (this.mouse.y - project.center.y) ** 2);
            if (dist <= project.maxRadius) {
                found = project;
                break;
            }
        }
        if (found !== this.hoveredConstellation) {
            this.hoveredConstellation = found;
            this.updateLabel(found);
        }
        this.canvas.style.cursor = found ? 'pointer' : 'default';
    }

    updateLabel(project) {
        let label = this.container.querySelector('.constellation-label');
        if (!label) {
            label = document.createElement('div');
            label.className = 'constellation-label';
            this.container.appendChild(label);
        }
        label.textContent = project ? project.name : '';
        if (project) {
            label.classList.add('visible');
        } else {
            label.classList.remove('visible');
        }
    }

    startZoom(project) {
        this.isZooming = true;
        this.zoomTarget = project;
        this.zoomStartTime = performance.now();
        this.zoomProgress = 0;
    }

    animate(currentTime) {
        if (!this.isActive) return;

        this.lastFrameTime = this.lastFrameTime || 0;
        const delta = currentTime - this.lastFrameTime;
        const interval = 1000 / this.fpsLimit;

        if (delta < interval) {
            requestAnimationFrame((t) => this.animate(t));
            return;
        }
        this.lastFrameTime = currentTime - (delta % interval);

        this.time += 0.016;
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        if (this.isZooming) {
            const elapsed = (performance.now() - this.zoomStartTime) / 1200;
            this.zoomProgress = this.easeInCubic(Math.min(elapsed, 1));
            if (elapsed >= 1) {
                window.open(this.zoomTarget.url, '_blank');
                this.isZooming = false;
                this.zoomProgress = 0;
                this.zoomTarget = null;
            }
        }

        this.drawBackgroundStars();
        this.drawCentralGalaxy();
        this.updateCenters();

        this.projectData.forEach(project => {
            const isHovered = this.hoveredConstellation === project;
            this.drawConstellation(project, isHovered ? 1 : 0.7, isHovered);
        });

        requestAnimationFrame((t) => this.animate(t));
    }

    easeInCubic(t) { return t * t * t; }
    easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

    getRevealState(projectIndex) {
        if (!this.revealStartTime) return { nodeProgress: 0, lineProgress: 0 };
        const elapsed = performance.now() - this.revealStartTime;
        const stagger = 480, nodePh = 1600, lineWait = 500, linePh = 1200;
        const localTime = elapsed - projectIndex * stagger;
        if (localTime <= 0) return { nodeProgress: 0, lineProgress: 0 };
        return {
            nodeProgress: Math.min(1, this.easeOutCubic(localTime / nodePh)),
            lineProgress: Math.min(1, this.easeOutCubic(Math.max(0, localTime - nodePh - lineWait) / linePh))
        };
    }

    drawBackgroundStars() {
        const cx = this.canvas.width / 2, cy = this.canvas.height / 2;
        this.bgStars.forEach(star => {
            star.phase += star.speed;
            let alpha = star.alpha * (0.5 + ((Math.sin(star.phase) + 1) / 2) * 0.5);
            let { x, y } = star, size = star.size;
            if (this.isZooming && this.zoomProgress > 0) {
                const stretch = 1 + this.zoomProgress * 4;
                x = cx + (x - cx) * stretch;
                y = cy + (y - cy) * stretch;
                size *= 1 + this.zoomProgress * 2;
                alpha = Math.min(1, alpha + this.zoomProgress * 0.5);
            }
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fillStyle = `rgba(180, 200, 255, ${alpha})`;
            this.ctx.fill();
        });
    }

    drawCentralGalaxy() {
        const cx = this.canvas.width / 2, cy = this.canvas.height / 2;
        const isMobile = this.canvas.width < 768;
        const scaleFactor = isMobile ? 0.4 : 1;
        const wobbleX = Math.sin(this.time * 0.3) * 5;
        const wobbleY = Math.cos(this.time * 0.4) * 5;

        let baseSize = this.hoveredConstellation && !this.isZooming ? 220 : 150;
        baseSize *= scaleFactor;
        let galaxyAlpha = (this.hoveredConstellation && !this.isZooming) ? 0.8 : (isMobile ? 0.3 : 0.6);

        if (this.isZooming) {
            baseSize = (150 * scaleFactor) + this.zoomProgress * 2000;
            galaxyAlpha = Math.max(galaxyAlpha, 0.6 + this.zoomProgress * 0.4);
        }

        const x = cx + wobbleX * (1 - this.zoomProgress);
        const y = cy + wobbleY * (1 - this.zoomProgress);
        this.drawSpiralGalaxy(x, y, baseSize, galaxyAlpha);

        // --- DRAW IMAGE IN CENTER ---
        if (this.hoveredConstellation?.imageLoaded && !this.isZooming) {
            this.drawImageAsGalaxy(this.hoveredConstellation, x, y, baseSize, galaxyAlpha);
        } else if (this.isZooming && this.zoomTarget?.imageLoaded) {
            this.drawImageAsGalaxy(this.zoomTarget, cx, cy, baseSize, galaxyAlpha);
        }
    }

    galaxyRgb(v) { return `rgb(${Math.round(v.x * 255)},${Math.round(v.y * 255)},${Math.round(v.z * 255)})`; }
    galaxyAdd(v, k) { return { x: v.x + k, y: v.y + k, z: v.z + k, w: v.w + k }; }
    lerp(a, b, p) { return a + (b - a) * p; }
    lerp4(a, b, p) { return { x: this.lerp(a.x, b.x, p), y: this.lerp(a.y, b.y, p), z: this.lerp(a.z, b.z, p), w: this.lerp(a.w, b.w, p) }; }
    galaxyGradient(colors, p) {
        for (let i = 1; i < colors.length; i++) {
            const prev = colors[i - 1], curr = colors[i];
            if (prev.w <= p && curr.w >= p) return this.lerp4(prev, curr, (p - prev.w) / (curr.w - prev.w));
        }
        return { x: 0, y: 0, z: 0, w: 0 };
    }
    galaxyLcg(v) { return ((v * 1103515245) + 12345) % Math.pow(2, 31); }

    drawSpiralGalaxy(centerX, centerY, size, alpha) {
        const RADIUS = size, INNER = size * 0.04, MOD = Math.pow(2, 31);
        const t = performance.now() / -60000;
        const darkHalo = this.ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, RADIUS * 1.5);
        darkHalo.addColorStop(0, 'rgba(4,9,17,0.7)');
        darkHalo.addColorStop(1, 'rgba(4,9,17,0)');
        this.ctx.fillStyle = darkHalo;
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, RADIUS * 1.5, 0, Math.PI * 2);
        this.ctx.fill();

        const grd = this.ctx.createRadialGradient(centerX, centerY, INNER, centerX, centerY, RADIUS);
        grd.addColorStop(1.0, 'rgba(0,0,0,0)');
        grd.addColorStop(0.5, `rgba(25,128,255,${0.15 * alpha})`);
        grd.addColorStop(0.2, `rgba(255,192,25,${0.25 * alpha})`);
        grd.addColorStop(0.0, `rgba(255,255,255,${alpha})`);
        this.ctx.save();
        this.ctx.fillStyle = grd;
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, RADIUS, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.restore();

        let v = 0;
        this.ctx.save();
        for (let arm = 0; arm < 3; arm++) {
            for (let stop = 0; stop < 1800; stop++) { // Optimized count
                v = this.galaxyLcg(v);
                const prog = stop / 1800, iprog = 1 - prog;
                const r = v / MOD, cr = (r - 0.5) * 2;
                const a = (prog * Math.PI * 2 * 1.61) + (Math.PI * 2 / 3 * arm);
                const b = t + a + Math.PI * 0.5 * (1 + iprog - 0.3) * cr;
                const d = Math.sqrt(prog) * RADIUS;
                const px = centerX + Math.cos(b) * d, py = centerY + Math.sin(b) * d;
                const s = (r - 0.5) * 2 + Math.pow(1.125, iprog * 8);
                this.ctx.save();
                this.ctx.globalAlpha = ((Math.sin((r + prog + t) * Math.PI * 2) + 1) * 0.5) * alpha;
                this.ctx.translate(px, py);
                this.ctx.fillStyle = this.galaxyRgb(this.galaxyAdd(this.galaxyGradient([
                    { x: 0.0, y: 0.25, z: 0.75, w: 0.0 }, { x: 0.1, y: 0.5, z: 1.0, w: 0.5 },
                    { x: 1.0, y: 0.75, z: 0.1, w: 0.8 }, { x: 1.0, y: 1.0, z: 1.0, w: 1.0 }
                ], iprog), r * 0.15));
                this.ctx.fillRect(-s * 0.5, -s * 0.5, s, s);
                this.ctx.restore();
            }
        }
        this.ctx.restore();

        const grd2 = this.ctx.createRadialGradient(centerX, centerY, 16, centerX, centerY, RADIUS);
        grd2.addColorStop(1.0, `rgba(0,64,128,${0.1 * alpha})`);
        grd2.addColorStop(0.0, `rgba(255,255,255,${alpha})`);
        this.ctx.save();
        this.ctx.globalCompositeOperation = 'lighter';
        this.ctx.fillStyle = grd2;
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY, RADIUS, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.restore();
    }

    drawImageAsGalaxy(project, x, y, size, alpha = 1) {
        const img = project.imageObj;
        const displayRadius = size * 0.7;
        this.ctx.save();
        this.ctx.translate(x, y);

        // Background glow for the image
        const bgGrad = this.ctx.createRadialGradient(0, 0, 0, 0, 0, displayRadius * 1.3);
        bgGrad.addColorStop(0, `rgba(20,40,80,${0.6 * alpha})`);
        bgGrad.addColorStop(1, 'rgba(0,0,0,0)');
        this.ctx.beginPath();
        this.ctx.arc(0, 0, displayRadius * 1.3, 0, Math.PI * 2);
        this.ctx.fillStyle = bgGrad;
        this.ctx.fill();

        // Clip and Draw Image
        this.ctx.beginPath();
        this.ctx.arc(0, 0, displayRadius, 0, Math.PI * 2);
        this.ctx.clip();

        this.ctx.globalCompositeOperation = 'source-over'; // ENSURE WE ARE OVERWRITING
        const aspect = img.width / img.height;
        const ts = displayRadius * 2;
        const dw = aspect > 1 ? ts * aspect : ts;
        const dh = aspect > 1 ? ts : ts / aspect;
        this.ctx.globalAlpha = 1.0 * alpha;

        this.ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
        this.ctx.restore();

        // Decorative ring
        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.globalAlpha = 0.25;
        const ring = this.ctx.createRadialGradient(0, 0, displayRadius * 0.95, 0, 0, displayRadius * 1.1);
        ring.addColorStop(0, 'rgba(0,212,255,0.5)');
        ring.addColorStop(1, 'rgba(0,0,0,0)');
        this.ctx.beginPath();
        this.ctx.arc(0, 0, displayRadius * 1.1, 0, Math.PI * 2);
        this.ctx.strokeStyle = ring;
        this.ctx.lineWidth = displayRadius * 0.15;
        this.ctx.stroke();
        this.ctx.restore();
    }

    drawConstellation(project, opacity, isHovered) {
        const ctx = this.ctx, center = project.center;
        const idx = this.projectData.indexOf(project);
        const reveal = this.getRevealState(idx);
        const isMobile = this.canvas.width < 768;
        const bgRadius = (project.maxRadius - 30 + 28) * (isMobile ? 0.65 : 1);
        const bgReveal = reveal.nodeProgress;

        if (project.imageLoaded) {
            ctx.save();
            ctx.translate(center.x, center.y);
            ctx.beginPath();
            ctx.arc(0, 0, bgRadius * 0.9, 0, Math.PI * 2);
            ctx.clip();
            ctx.globalAlpha = (isMobile || this.isTouch ? 0.45 : 0.25) * bgReveal * (isHovered ? 1.4 : 1);
            const img = project.imageObj;
            const aspect = img.width / img.height;
            const ts = bgRadius * 1.8;
            const dw = aspect > 1 ? ts * aspect : ts, dh = aspect > 1 ? ts : ts / aspect;
            ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
            ctx.restore();

            ctx.beginPath();
            ctx.arc(center.x, center.y, bgRadius * 0.9, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(0, 212, 255, ${0.1 * bgReveal})`;
            ctx.lineWidth = 1;
            ctx.stroke();
        }

        if (!isMobile && !this.isTouch) {
            const bgGrad = ctx.createRadialGradient(center.x, center.y, bgRadius * 0.1, center.x, center.y, bgRadius);
            bgGrad.addColorStop(0, `rgba(5,18,45,${(isHovered ? 0.82 : 0.72) * bgReveal})`);
            bgGrad.addColorStop(0.85, `rgba(10,30,80,${(isHovered ? 0.45 : 0.30) * bgReveal})`);
            bgGrad.addColorStop(1, `rgba(0,10,30,0)`);
            ctx.beginPath();
            ctx.arc(center.x, center.y, bgRadius, 0, Math.PI * 2);
            ctx.fillStyle = bgGrad;
            ctx.fill();
        }

        const colors = this.colors;
        project.connections.forEach(([i1, i2], lineIndex) => {
            const slot = 1 / project.connections.length;
            const localProg = Math.max(0, Math.min(1, (reveal.lineProgress - lineIndex * slot) / slot));
            if (localProg <= 0) return;
            const n1 = project.nodes[i1], n2 = project.nodes[i2];
            const x1 = center.x + n1.x, y1 = center.y + n1.y;
            const x2 = center.x + n2.x, y2 = center.y + n2.y;
            const ex = x1 + (x2 - x1) * localProg, ey = y1 + (y2 - y1) * localProg;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(ex, ey);
            ctx.strokeStyle = isHovered ? colors.connectionHover : colors.connectionBase;
            ctx.lineWidth = isHovered ? 2.5 : 1.5;
            ctx.globalAlpha = opacity * 0.8 * localProg;
            ctx.stroke();
            ctx.globalAlpha = 1;
        });

        project.nodes.forEach((node, index) => {
            const slot = 1 / project.nodes.length;
            const localProg = Math.max(0, Math.min(1, (reveal.nodeProgress - index * slot) / slot));
            if (localProg <= 0) return;
            const x = center.x + node.x, y = center.y + node.y;
            const size = node.size * (isHovered ? 1.2 : 1) * (this.canvas.width < 768 ? 0.85 : 1);
            const pulse = Math.sin(this.time * 2 + index) * 0.2 + 1;
            const finalSize = size * pulse * (localProg < 1 ? 1 + Math.sin(localProg * Math.PI) * 0.5 : 1);
            const nodeAlpha = opacity * Math.min(1, localProg * 2);

            ctx.beginPath();
            ctx.arc(x, y, finalSize, 0, Math.PI * 2);
            ctx.fillStyle = isHovered ? colors.nodeActive : colors.nodePrimary;
            ctx.globalAlpha = nodeAlpha;
            ctx.fill();
            ctx.globalAlpha = 1;
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
        new ConstellationWorks('constellation-canvas');
    }, 1100);
});
export default ConstellationWorks;